<template>
  <section
    class="return-voucher-wrapper"
    aria-label="Détail du bon de retour logistique"
    role="main"
  >
    <!-- Alert: No item found -->
    <b-alert
      variant="danger"
      :show="returnVoucher === undefined"
      class="modern-alert d-print-none"
      role="alert"
      aria-live="assertive"
      aria-label="Aucun bon de retour trouvé"
    >
      <div class="d-flex align-items-center">
        <feather-icon
          icon="AlertCircleIcon"
          size="24"
          class="mr-2"
          aria-hidden="true"
        />
        <div>
          <h4 class="alert-heading mb-1">
            Erreur lors du chargement
          </h4>
          <div class="alert-body">
            Aucun bon de retour trouvé avec cet ID. Consultez la
            <b-link
              class="alert-link"
              :to="{ name: 'return-vouchers-list' }"
              aria-label="Retour à la liste des bons de retour logistiques"
            >
              liste des retours
            </b-link>
            pour d'autres bons.
          </div>
        </div>
      </div>
    </b-alert>

    <!-- Loading State -->
    <div
      v-if="isLoading"
      class="loading-container d-print-none"
      aria-busy="true"
      aria-live="polite"
    >
      <b-spinner
        variant="primary"
        label="Chargement du bon de retour logistique..."
        class="loading-spinner"
        aria-label="Chargement du bon de retour logistique..."
      />
      <p class="loading-text mt-3">
        Chargement du bon de retour logistique...
      </p>
    </div>

    <!-- Main Content -->
    <div
      v-if="!isLoading && returnVoucher"
      aria-label="Contenu principal du bon de retour logistique"
      role="region"
    >
      <!-- Top Action Bar -->
      <div class="top-action-bar mb-4 d-print-none">
        <b-row class="align-items-center">
          <b-col
            cols="12"
            md="6"
          >
            <h2 class="page-title mb-0">
              <feather-icon
                icon="PackageIcon"
                size="24"
                class="mr-2"
              />
              Bon de retour #{{ returnVoucher.id }}
            </h2>
            <p class="page-subtitle">
              Créé le {{ formatFn(returnVoucher.created_at) }}
            </p>
          </b-col>
          <b-col
            cols="12"
            md="6"
            class="text-md-right"
          >
            <div class="action-buttons-top">
              <div class="view-mode-toggle mr-3">
                <span class="view-mode-label">Aperçu</span>
                <b-button-group size="sm">
                  <b-button
                    :variant="visualMode === 'color' ? 'primary' : 'outline-secondary'"
                    @click="setVisualMode('color')"
                  >
                    <feather-icon
                      icon="ApertureIcon"
                      size="14"
                      class="mr-1"
                    />
                    Couleur
                  </b-button>
                  <b-button
                    :variant="visualMode === 'bw' ? 'dark' : 'outline-secondary'"
                    @click="setVisualMode('bw')"
                  >
                    <feather-icon
                      icon="PrinterIcon"
                      size="14"
                      class="mr-1"
                    />
                    N&B
                  </b-button>
                </b-button-group>
              </div>
              <b-dropdown
                v-ripple.400="'rgba(var(--brand-primary-rgb), 0.15)'"
                variant="outline-primary"
                class="action-btn-top"
                :disabled="isLoading"
                text="Imprimer"
                right
              >
                <template #button-content>
                  <feather-icon
                    icon="PrinterIcon"
                    size="16"
                    class="mr-2"
                  />
                  Imprimer
                  <feather-icon
                    icon="ChevronDownIcon"
                    size="14"
                    class="ml-1"
                  />
                </template>

                <b-dropdown-item
                  v-b-tooltip.hover.right="'Imprimer une version simplifiée en noir et blanc, sans la colonne Motifs de retour.'"
                  @click="printVoucherBW"
                >
                  <feather-icon
                    icon="PrinterIcon"
                    size="16"
                    class="mr-2"
                  />
                  Version noir et blanc
                </b-dropdown-item>

                <b-dropdown-item
                  v-b-tooltip.hover.right="'Imprimer le bon de retour en couleur avec tous les éléments graphiques.'"
                  @click="printVoucher"
                >
                  <feather-icon
                    icon="PrinterIcon"
                    size="16"
                    class="mr-2"
                  />
                  Version couleur
                </b-dropdown-item>
              </b-dropdown>

              <!-- Quick Stats -->
              <div class="quick-stats d-inline-flex ml-3">
                <div class="stat-item-inline info">
                  <feather-icon
                    icon="PackageIcon"
                    size="14"
                  />
                  <span>{{ returnVoucher.orders_count }}</span>
                </div>
                <div class="stat-item-inline danger">
                  <feather-icon
                    icon="DollarSignIcon"
                    size="14"
                  />
                  <span>{{ returnVoucher.total_return_price }} DT</span>
                </div>
                <div class="stat-item-inline primary">
                  <feather-icon
                    icon="TrendingUpIcon"
                    size="14"
                  />
                  <span>{{ returnVoucher.total_price }} DT</span>
                </div>
                <div :class="['stat-item-inline', returnVoucher.is_validated ? 'success' : 'warning']">
                  <feather-icon
                    :icon="returnVoucher.is_validated ? 'CheckCircleIcon' : 'ClockIcon'"
                    size="14"
                  />
                  <span>{{ returnVoucher.is_validated ? 'Validé' : 'En attente' }}</span>
                </div>
              </div>
            </div>
          </b-col>
        </b-row>
      </div>

      <!-- Voucher Content - This is what gets printed -->
      <div
        id="voucher-content"
        class="voucher-content"
      >
        <!-- Full Width Voucher Document -->
        <b-card :class="['voucher-card shadow-sm', voucherCardClasses]">
          <!-- Header Section -->
          <div class="voucher-header">
            <b-row class="align-items-center">
              <b-col
                cols="12"
                md="6"
                class="mb-3 mb-md-0"
              >
                <div class="company-logo">
                  <brand-logo
                    :monochrome="visualMode === 'bw'"
                    class="logo-img"
                  />
                </div>
              </b-col>
              <b-col
                cols="12"
                md="6"
                class="text-md-right"
              >
                <h1 class="voucher-title">
                  Bon de retour N°
                  <span class="voucher-number">{{ returnVoucher.id }}</span>
                </h1>
                <div class="voucher-date">
                  <feather-icon
                    icon="CalendarIcon"
                    size="16"
                    class="mr-1"
                  />
                  {{ formatFn(returnVoucher.created_at) }}
                </div>
              </b-col>
            </b-row>
          </div>

          <!-- Company Information & Supplier Info -->
          <b-row class="company-info-section">
            <b-col
              cols="12"
              md="6"
            >
              <document-company-info
                :company="company"
                header-title="Informations de la société"
                custom-tagline="Service de retour des colis"
              >
                <template #icon>
                  <feather-icon
                    icon="BuildingIcon"
                    size="20"
                    class="mr-2"
                  />
                </template>
              </document-company-info>
            </b-col>
            <b-col
              cols="12"
              md="6"
            >
              <b-card class="info-card">
                <div class="info-header">
                  <feather-icon
                    icon="UserIcon"
                    size="20"
                    class="mr-2"
                  />
                  <h5 class="mb-0">
                    Fournisseur
                  </h5>
                </div>
                <div class="info-content">
                  <div class="info-item">
                    <span class="info-label">Nom:</span>
                    <span>{{ returnVoucher.supplier && returnVoucher.supplier.username ? returnVoucher.supplier.username : '-' }}</span>
                  </div>
                  <div
                    v-if="returnVoucher.supplier && returnVoucher.supplier.email"
                    class="info-item"
                  >
                    <span class="info-label">Email:</span>
                    <span>{{ returnVoucher.supplier.email }}</span>
                  </div>
                  <div
                    v-if="returnVoucher.supplier && returnVoucher.supplier.phone"
                    class="info-item"
                  >
                    <span class="info-label">Téléphone:</span>
                    <span>{{ returnVoucher.supplier.phone }}</span>
                  </div>
                </div>
              </b-card>
            </b-col>
          </b-row>

          <!-- Summary Table -->
          <div class="summary-section">
            <h5 class="section-title">
              <feather-icon
                icon="BarChartIcon"
                size="20"
                class="mr-2"
              />
              Résumé du bon de retour
            </h5>
            <div class="summary-table-container">
              <div class="table-responsive">
                <table class="modern-table">
                  <thead>
                    <tr>
                      <th>Nombre de colis</th>
                      <th>Date de création</th>
                      <th>Statut de validation</th>
                      <th>Date de validation</th>
                      <th>Total TTC</th>
                      <th>Total frais de retour</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <b-badge
                          variant="info"
                          class="stat-badge"
                        >
                          {{ returnVoucher.orders_count }}
                        </b-badge>
                      </td>
                      <td>{{ formatFn(returnVoucher.created_at) }}</td>
                      <td>
                        <b-badge :variant="returnVoucher.is_validated ? 'success' : 'warning'">
                          {{ returnVoucher.is_validated ? 'Validé' : 'En attente' }}
                        </b-badge>
                      </td>
                      <td>
                        <span v-if="returnVoucher.validated_at">
                          {{ formatFn(returnVoucher.validated_at) }}
                        </span>
                        <span
                          v-else
                          class="text-muted"
                        >-</span>
                      </td>
                      <td class="amount">
                        {{ returnVoucher.total_price }} DT
                      </td>
                      <td class="amount danger">
                        {{ returnVoucher.total_return_price }} DT
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Orders Table -->
          <div class="orders-section">
            <div class="section-header">
              <h5 class="section-title">
                <feather-icon
                  icon="PackageIcon"
                  size="20"
                  class="mr-2"
                />
                Détails des colis retournés
                <b-badge
                  variant="light-primary"
                  class="ml-2"
                >
                  {{ returnVoucher.orders_count }} colis
                </b-badge>
              </h5>
            </div>

            <div class="table-container">
              <b-table-lite
                responsive
                class="modern-orders-table"
                :items="orders"
                :fields="[
                  { key: 'BL', label: 'N° Colis' },
                  { key: 'zone_adresse', label: 'Adresse de livraison' },
                  { key: 'tel', label: 'Téléphone' },
                  { key: 'destinataire', label: 'Destinataire' },
                  { key: 'prix', label: 'Prix TTC' },
                  { key: 'prix_de_retour', label: 'Frais retour' },
                  { key: 'motifs_retour', label: 'Motifs de retour', thClass: 'motifs-column', tdClass: 'motifs-column' }
                ]"
                striped
                hover
              >
                <!-- Order ID Column -->
                <template #cell(BL)="data">
                  <div class="order-id-cell">
                    <strong class="order-number">#{{ data.item.BL }}</strong>
                    <b-badge
                      v-if="data.item.is_an_exchange"
                      variant="warning"
                      class="exchange-badge"
                    >
                      Échange
                    </b-badge>
                  </div>
                </template>

                <!-- Address Column -->
                <template #cell(zone_adresse)="data">
                  <div class="address-cell">
                    <div class="address-item">
                      <feather-icon
                        icon="MapPinIcon"
                        size="14"
                        class="mr-1"
                      />
                      <strong>{{ data.item.zone_adresse.governorate }}</strong>
                    </div>
                    <div class="address-item">
                      <span class="text-muted">{{ data.item.zone_adresse.delegation }}</span>
                    </div>
                    <div class="address-item">
                      <span class="text-muted">{{ data.item.zone_adresse.locality }}</span>
                    </div>
                    <div class="address-item">
                      <small class="text-muted">{{ data.item.zone_adresse.address }}</small>
                    </div>
                  </div>
                </template>

                <!-- Phone Column -->
                <template #cell(tel)="data">
                  <div class="phone-cell">
                    <div class="phone-item">
                      <feather-icon
                        icon="PhoneIcon"
                        size="14"
                        class="mr-1"
                      />
                      {{ data.item.tel }}
                    </div>
                    <div
                      v-if="data.item.tel2"
                      class="phone-item"
                    >
                      <feather-icon
                        icon="PhoneIcon"
                        size="14"
                        class="mr-1"
                      />
                      {{ data.item.tel2 }}
                    </div>
                  </div>
                </template>

                <!-- Customer Column -->
                <template #cell(destinataire)="data">
                  <div class="customer-cell">
                    <feather-icon
                      icon="UserIcon"
                      size="16"
                      class="mr-2 text-primary"
                    />
                    <span class="customer-name">{{ data.item.destinataire }}</span>
                  </div>
                </template>

                <!-- Price Column -->
                <template #cell(prix)="data">
                  <div class="price-cell">
                    <span class="price-amount">{{ data.item.prix }} DT</span>
                  </div>
                </template>

                <!-- Return Price Column -->
                <template #cell(prix_de_retour)="data">
                  <div class="return-price-cell">
                    <span class="return-price-amount">{{ data.item.prix_de_retour }} DT</span>
                  </div>
                </template>

                <!-- Return Reasons Column -->
                <template #cell(motifs_retour)="data">
                  <div class="return-reasons-cell">
                    <div
                      v-if="data.item.return_reasons && data.item.return_reasons.length"
                      class="reasons-list"
                    >
                      <div
                        v-for="(reason, idx) in data.item.return_reasons"
                        :key="idx"
                        class="reason-item"
                      >
                        <div class="reason-main">
                          <div class="reason-text">
                            <strong class="reason-title">{{ reason.return_reason }}</strong>
                            <div
                              v-if="reason.additional_note"
                              class="reason-note"
                            >
                              <feather-icon
                                icon="MessageSquareIcon"
                                size="12"
                                class="mr-1"
                              />
                              {{ reason.additional_note }}
                            </div>
                          </div>

                          <div class="reason-meta">
                            <div
                              v-if="reason.created_at"
                              class="reason-date"
                            >
                              <feather-icon
                                icon="ClockIcon"
                                size="12"
                                class="mr-1"
                              />
                              <span class="date-label">Signalé:</span>
                              <span class="date-value">{{ formatDateOnly(reason.created_at) }}</span>
                            </div>
                            <div
                              v-if="reason.scheduled_date"
                              class="reason-date scheduled"
                            >
                              <feather-icon
                                icon="CalendarIcon"
                                size="12"
                                class="mr-1"
                              />
                              <span class="date-label">Planifié:</span>
                              <span class="date-value">{{ formatDateOnly(reason.scheduled_date) }}</span>
                            </div>
                          </div>

                          <div
                            v-if="reason.quick_options && reason.quick_options.length"
                            class="reason-options"
                          >
                            <div class="options-label">
                              Options:
                            </div>
                            <div class="options-list">
                              <b-badge
                                v-for="(option, optionIdx) in reason.quick_options"
                                :key="optionIdx"
                                variant="outline-secondary"
                                class="option-badge"
                              >
                                {{ option }}
                              </b-badge>
                            </div>
                          </div>
                        </div>

                        <div
                          v-if="idx < data.item.return_reasons.length - 1"
                          class="reason-divider"
                        />
                      </div>
                    </div>
                    <div
                      v-else
                      class="no-reason"
                    >
                      <feather-icon
                        icon="MinusIcon"
                        size="14"
                        class="mr-1 text-muted"
                      />
                      <span class="text-muted">Aucun motif spécifié</span>
                    </div>
                  </div>
                </template>
              </b-table-lite>
            </div>
          </div>

          <!-- Signature Section -->
          <document-signature
            left-label="Signature du responsable"
            :company="company"
          />

        </b-card>
      </div>
    </div>
  </section>
</template>

<script>
import {
  BRow,
  BCol,
  BCard,
  BTableLite,
  BAlert,
  BLink,
  BBadge,
  BSpinner,
  BDropdown,
  BDropdownItem,
  BButton,
  BButtonGroup,
  VBToggle,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import axios from 'axios'
import storeAuth from '@/store/store'
import { DocumentCompanyInfo, DocumentSignature } from '@/components/document'

export default {
  directives: {
    Ripple,
    'b-toggle': VBToggle,
  },
  components: {
    BRow,
    BCol,
    BCard,
    BTableLite,
    BAlert,
    BLink,
    BBadge,
    BSpinner,
    BDropdown,
    BDropdownItem,
    BButton,
    BButtonGroup,
    DocumentCompanyInfo,
    DocumentSignature,
  },
  data() {
    return {
      returnVoucher: [],
      orders: [],
      company: {},
      isLoading: false,
      visualMode: 'color', // 'color' or 'bw'
    }
  },
  computed: {
    netToReturn() {
      if (!this.returnVoucher || !this.returnVoucher.total_price) {
        return '0.00'
      }
      return (this.returnVoucher.total_price - this.returnVoucher.total_return_price).toFixed(2)
    },
    voucherCardClasses() {
      return {
        'voucher-card--bw': this.visualMode === 'bw',
        'voucher-card--color': this.visualMode === 'color',
      }
    },
  },
  created() {
    this.company = storeAuth.state.company
    this.id = this.$route.params.id
    this.getReturnVoucher()
  },
  methods: {
    formatFn(date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      const h = date.slice(11, 19)
      return `${year}-${month.padStart(2, '0')}-${day1.padStart(2, '0')} | ${h}`
    },
    formatDateOnly(date) {
      if (!date) {
        return null
      }
      const [year, month, day] = date.split('-')
      const day1 = day.substr(0, 2)
      return `${day1.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`
    },
    async getReturnVoucher() {
      this.isLoading = true
      try {
        const { data } = await axios.get(`/api/return-vouchers/detail/${this.$route.params.id}`)
        this.returnVoucher = data
        this.orders = []
        const { orders } = this.returnVoucher
        for (let i = 0; i < orders.length; i += 1) {
          const order = orders[i]
          const dataObj = {
            code: order.display_cross_tenant_barcode && order.cross_tenant_barcode ? order.cross_tenant_barcode : order.id,
            gouvernorat: order.governorate,
            // fournisseur: order.supplier.username, // removed, now shown above table
            tel: order.phone1,
            tel2: order.phone2 || '',
            destinataire: order.name,
            adresse: order.address,
            prix: order.price_ttc,
            prix_de_retour: order.price_return,
            is_an_exchange: order.is_an_exchange,
            return_reasons: order.all_return_reasons || order.return_reasons || [],
            BL: order.display_cross_tenant_barcode && order.cross_tenant_barcode ? order.cross_tenant_barcode : order.id,
            zone_adresse: {
              governorate: order.governorate,
              delegation: order.delegation,
              locality: order.locality || order.locality,
              address: order.address,
            },
          }
          this.orders.push(dataObj)
        }
      } catch (e) {
        // handle error if needed
      }
      this.isLoading = false
    },
    buildPrintCSS(isBW) {
      const c = isBW ? {
        printColorAdjust: '',
        bodyFontSize: '12px',
        fontSize: '12.5px',
        lineHeight: '1.4',
        accent: '#000',
        accentLight: '#aaa',
        headerBg: '#fff',
        headerBorder: '2px solid #000',
        headerBorderLeft: '2px solid #000',
        logoMaxWidth: '95px',
        logoMaxHeight: '30px',
        voucherNumberColor: '#000',
        voucherDateColor: '#000',
        sectionBorder: '#000',
        tableBorder: '#000',
        tableThBg: '#fff',
        tableThColor: '#000',
        ordersThBg: '#fff',
        ordersThColor: '#000',
        ordersTdBorder: '#000',
        badgeBg: '#fff',
        badgeColor: '#000',
        badgeBorder: '#000',
        badgeSuccessBg: '#fff',
        badgeSuccessColor: '#000',
        badgeWarningBg: '#fff',
        badgeWarningColor: '#000',
        badgeWarningBorderColor: '#000',
        reasonMainBg: '#fff',
        reasonMainBorderLeft: '#000',
        reasonTitleColor: '#000',
        optionBadgeBorder: '#000',
        reasonDividerBg: '#aaa',
        totalsBg: '#fff',
        totalsBorder: '#000',
        totalHighlightBg: '#eaeaea',
        totalHighlightColor: '#000',
        totalHighlightLabelColor: '#000',
      } : {
        printColorAdjust: '* { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }',
        bodyFontSize: '13px',
        fontSize: '13.5px',
        lineHeight: '1.35',
        accent: 'var(--brand-secondary)',
        accentLight: '#9eafbb',
        headerBg: 'linear-gradient(135deg, #f4f8fb 0%, #ebf1f5 100%)',
        headerBorder: '1px solid #9eafbb',
        headerBorderLeft: '5px solid var(--brand-secondary)',
        logoMaxWidth: '105px',
        logoMaxHeight: '34px',
        voucherNumberColor: 'var(--brand-secondary)',
        voucherDateColor: '#263238',
        sectionBorder: '#9eafbb',
        tableBorder: '#b9c4cc',
        tableThBg: 'var(--brand-secondary)',
        tableThColor: 'white',
        ordersThBg: '#e7eef3',
        ordersThColor: '#111',
        ordersTdBorder: '#b9c4cc',
        badgeBg: '#edf4f8',
        badgeColor: 'var(--brand-secondary)',
        badgeBorder: 'var(--brand-secondary)',
        badgeSuccessBg: '#e8f5ec',
        badgeSuccessColor: '#175431',
        badgeWarningBg: '#fff4de',
        badgeWarningColor: '#7a5200',
        badgeWarningBorderColor: '#a06a00',
        reasonMainBg: '#f7fafc',
        reasonMainBorderLeft: 'var(--brand-secondary)',
        reasonTitleColor: 'var(--brand-secondary)',
        optionBadgeBorder: '#9eafbb',
        reasonDividerBg: '#c7d1d8',
        totalsBg: '#f4f7f9',
        totalsBorder: '#9eafbb',
        totalHighlightBg: 'var(--brand-secondary)',
        totalHighlightColor: 'white',
        totalHighlightLabelColor: 'white',
      }

      return `
        <style id="print-styles">
          @page { margin: 12mm 16mm 14mm; padding: 0; size: auto; }
          @media print {
            ${c.printColorAdjust}
            html, body {
              margin: 0 !important; padding: 0 !important; height: auto !important;
              background-color: white !important; font-family: Arial, sans-serif;
              font-size: ${c.bodyFontSize}; line-height: ${c.lineHeight}; color: #000;
            }
            body * { visibility: hidden; }
            #voucher-content, #voucher-content * { visibility: visible; }
            #voucher-content {
              position: relative !important; left: auto !important; right: auto !important;
              top: auto !important; width: 100% !important; max-width: 100% !important;
              margin: 0 auto !important; padding: 0 3mm !important;
              box-sizing: border-box !important; overflow: visible !important;
              font-family: Arial, sans-serif; font-size: ${c.fontSize}; color: #000;
            }
            * { transition: none !important; animation: none !important; transform: none !important; }
            .return-voucher-wrapper { display: block !important; padding: 0 !important; margin: 0 !important; background: transparent !important; min-height: auto !important; }
            main, section, #app { margin: 0 !important; padding: 0 !important; }
            .voucher-card { border: none !important; box-shadow: none !important; margin: 0 !important; padding: 0 !important; background: white !important; border-radius: 0 !important; }
            .voucher-card .card-body, .card-body { padding: 0 !important; }
            .table-responsive { overflow: visible !important; }
            .content.app-content, .app-content, .content-wrapper, .content-body { margin: 0 !important; padding: 0 !important; }
            .top-action-bar, .d-print-none, .modern-alert, .feather-icon, .no-print, .motifs-column { display: none !important; }
            nav, .main-menu, .header-navbar-shadow, .customizer-toggle, footer.footer,
            .navbar-header, .navbar-brand, .brand-logo, .brand-text, .collapsed-logo, .full-logo { display: none !important; }
            .navbar-header img, .navbar-brand img, .brand-logo img, .collapsed-logo-img { display: none !important; }
            .voucher-header {
              display: flex !important; flex-direction: row !important; align-items: center !important;
              justify-content: space-between !important; margin: 0 0 10px 0 !important;
              padding: 8px 12px !important; background: ${c.headerBg} !important;
              border: ${c.headerBorder} !important; border-left: ${c.headerBorderLeft} !important;
              border-radius: 0 !important; width: 100% !important;
            }
            .voucher-header .row { display: flex !important; flex-direction: row !important; width: 100% !important; margin: 0 !important; align-items: center !important; }
            .voucher-header .row > [class*="col"] { flex: 0 0 auto !important; width: auto !important; max-width: none !important; padding: 0 !important; margin: 0 !important; }
            .voucher-header .row > .col-md-6:first-child { flex: 0 0 auto !important; }
            .voucher-header .row > .col-md-6:last-child { flex: 1 !important; text-align: right !important; }
            .company-logo { padding: 0 !important; margin: 0 !important; background: transparent !important; }
            .logo-img { max-width: ${c.logoMaxWidth} !important; max-height: ${c.logoMaxHeight} !important; width: auto !important; height: auto !important; display: block !important; object-fit: contain !important; }
            .voucher-card--bw .company-logo .logo-img { border: none !important; padding: 0 !important; background: transparent !important; box-shadow: none !important; border-radius: 0 !important; outline: none !important; }
            .voucher-title { font-size: 18px !important; font-weight: bold !important; margin: 0 !important; color: #1a1a1a !important; line-height: 1.2 !important; }
            .voucher-number { color: ${c.voucherNumberColor} !important; }
            .voucher-date { font-size: 11.5px !important; color: ${c.voucherDateColor} !important; margin: 2px 0 0 0 !important; }
            .company-info-section { display: flex !important; flex-wrap: wrap !important; margin-bottom: 12px !important; gap: 0 !important; align-items: stretch !important; page-break-inside: avoid !important; }
            .company-info-section > [class*="col"] { display: flex !important; flex: 0 0 50% !important; max-width: 50% !important; padding: 0 6px !important; }
            .row { display: flex !important; flex-wrap: wrap !important; margin: 0 !important; }
            .col-md-6 { flex: 0 0 50% !important; max-width: 50% !important; padding: 0 4px !important; }
            .info-card { display: flex !important; flex-direction: column !important; justify-content: flex-start !important; width: 100% !important; height: 100% !important; padding: 11px 13px !important; margin-bottom: 6px !important; border: 1px solid #000 !important; border-left: 4px solid #000 !important; background: #fff !important; }
            .company-card { background: #fff !important; border: 1px solid #000 !important; border-left: 4px solid #000 !important; box-shadow: none !important; }
            .info-header { font-size: 13px !important; font-weight: 700 !important; margin-bottom: 6px !important; padding-bottom: 4px !important; border-bottom: 1px solid #000 !important; color: #000 !important; }
            .company-card .info-header h5, .company-card .company-name, .company-card .info-subtitle, .company-card .info-label { color: #000 !important; }
            .info-item { font-size: 12px !important; margin-bottom: 4px !important; line-height: 1.45 !important; color: #111 !important; }
            .company-card .info-item, .info-card .info-item { display: flex !important; flex-direction: row !important; column-gap: 6px !important; align-items: baseline !important; flex-wrap: nowrap !important; overflow: visible !important; }
            .info-label { font-weight: 700 !important; flex-shrink: 0 !important; white-space: nowrap !important; min-width: 90px !important; color: #000 !important; }
            .company-card .info-value--nowrap, .info-card .info-value--nowrap { white-space: nowrap !important; flex-shrink: 1 !important; }
            .company-card .info-item > span:last-child, .info-card .info-item > span:last-child { word-break: normal !important; overflow-wrap: normal !important; }
            .company-card .company-name-block { display: block !important; margin-bottom: 6px !important; }
            .company-card .company-name { display: block !important; font-weight: bold !important; font-size: 13px !important; color: #000 !important; white-space: normal !important; }
            .company-card .info-subtitle { display: block !important; font-size: 11px !important; color: #000 !important; }
            .company-card .info-content { display: block !important; }
            .summary-section { margin-bottom: 12px !important; }
            .orders-section { margin-bottom: 10px !important; }
            .section-title { font-size: 15px !important; font-weight: bold !important; margin: 0 0 8px 0 !important; padding-bottom: 4px !important; border-bottom: 1px solid ${c.sectionBorder} !important; color: #111 !important; page-break-after: avoid !important; }
            .section-header { margin-bottom: 8px !important; page-break-after: avoid !important; }
            .summary-table-container { margin-bottom: 10px !important; border: 1px solid ${c.tableBorder} !important; border-radius: 6px !important; overflow: hidden !important; page-break-inside: avoid !important; }
            .table-container { margin-bottom: 6px !important; border: none !important; border-radius: 0 !important; box-shadow: none !important; background: transparent !important; overflow: visible !important; page-break-inside: auto !important; }
            .table-container .table-responsive { border: none !important; box-shadow: none !important; overflow: visible !important; }
            .modern-table, .modern-orders-table { font-size: 11.5px !important; width: 100% !important; border-collapse: collapse !important; table-layout: auto !important; }
            .modern-table th { background: ${c.tableThBg} !important; color: ${c.tableThColor} !important; font-size: 11.5px !important; padding: 8px 9px !important; text-align: center !important; border: 1px solid ${c.accent} !important; }
            .modern-table td { font-size: 11.5px !important; padding: 8px 9px !important; text-align: center !important; vertical-align: middle !important; border: 1px solid ${c.tableBorder} !important; background: #fff !important; }
            .modern-orders-table th { background: ${c.ordersThBg} !important; color: ${c.ordersThColor} !important; font-size: 11px !important; padding: 8px 7px !important; border: 1px solid ${c.accentLight} !important; white-space: normal !important; }
            .modern-orders-table td { font-size: 10.5px !important; padding: 7px !important; border: 1px solid ${c.ordersTdBorder} !important; vertical-align: top !important; white-space: normal !important; color: #111 !important; }
            .modern-orders-table thead { display: table-header-group !important; }
            .modern-orders-table th:nth-child(1), .modern-orders-table td:nth-child(1),
            .modern-orders-table th:nth-child(3), .modern-orders-table td:nth-child(3),
            .modern-orders-table th:nth-child(5), .modern-orders-table td:nth-child(5),
            .modern-orders-table th:nth-child(6), .modern-orders-table td:nth-child(6) { white-space: nowrap !important; }
            .modern-orders-table tbody tr, .modern-table tbody tr { page-break-inside: avoid !important; }
            .badge, .stat-badge { background: ${c.badgeBg} !important; color: ${c.badgeColor} !important; border: 1px solid ${c.badgeBorder} !important; padding: 2px 7px !important; font-size: 10.5px !important; font-weight: 700 !important; }
            .badge-success { background: ${c.badgeSuccessBg} !important; color: ${c.badgeSuccessColor} !important; border-color: ${c.badgeSuccessColor} !important; }
            .badge-warning { background: ${c.badgeWarningBg} !important; color: ${c.badgeWarningColor} !important; border-color: ${c.badgeWarningBorderColor} !important; }
            .price-amount, .return-price-amount, .amount, .amount.danger, .total-value.primary, .total-value.danger { color: #111 !important; font-weight: 700 !important; }
            .address-item, .phone-item, .customer-cell, .order-number, .reason-date, .reason-note, .options-label, .no-reason { color: #111 !important; }
            .return-reasons-cell { min-width: 0 !important; max-width: none !important; }
            .reason-main { background: ${c.reasonMainBg} !important; border: 1px solid ${c.tableBorder} !important; border-left: 4px solid ${c.reasonMainBorderLeft} !important; border-radius: 6px !important; padding: 8px 10px !important; page-break-inside: avoid !important; }
            .reason-title { color: ${c.reasonTitleColor} !important; font-size: 11px !important; font-weight: 700 !important; }
            .reason-note { font-size: 10px !important; }
            .reason-date, .options-label, .date-label, .date-value { font-size: 9.5px !important; }
            .option-badge { background: #fff !important; color: #111 !important; border: 1px solid ${c.optionBadgeBorder} !important; font-size: 8.5px !important; padding: 2px 6px !important; }
            .reason-divider { background: ${c.reasonDividerBg} !important; margin: 8px 0 !important; }
            .totals-footer { margin-top: 12px !important; padding: 10px 12px !important; background: ${c.totalsBg} !important; border: 1px solid ${c.totalsBorder} !important; border-radius: 6px !important; page-break-inside: avoid !important; }
            .totals-grid { display: flex !important; justify-content: space-around !important; gap: 12px !important; }
            .total-item { text-align: center !important; padding: 8px 12px !important; background: white !important; border-radius: 6px !important; border: 1px solid ${c.accentLight} !important; }
            .total-item.highlight { background: ${c.totalHighlightBg} !important; color: ${c.totalHighlightColor} !important; border-color: ${c.totalHighlightBg} !important; }
            .total-label { font-size: 10.5px !important; font-weight: 600 !important; }
            .total-value { font-size: 15px !important; font-weight: bold !important; color: #111 !important; }
            .total-value.primary, .total-value.danger { color: #111 !important; }
            .highlight .total-label, .highlight .total-value { color: ${c.totalHighlightLabelColor} !important; }
            .signature-section { margin-top: 12px !important; page-break-inside: avoid !important; }
            .signature-title { font-weight: bold !important; color: #000 !important; margin-bottom: 4px !important; font-size: 10.5px !important; text-align: center !important; }
            .signature-line { height: 32px !important; border-bottom: 1px solid #000 !important; margin-bottom: 4px !important; background: #fff !important; }
            .signature-section .row { margin-bottom: 0 !important; }
            .summary-section, .totals-footer, .signature-section { page-break-inside: avoid !important; }
            tr, td, th { break-inside: avoid !important; }
            tr { page-break-inside: avoid !important; }
          }
        </style>
      `
    },
    executePrint(mode) {
      const previousMode = this.visualMode
      const originalTitle = document.title
      const faviconEl = document.querySelector("link[rel~='icon'], link[rel='shortcut icon']")
      const originalFaviconHref = faviconEl ? faviconEl.getAttribute('href') : null
      if (faviconEl) {
        faviconEl.setAttribute('href', "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg'></svg>")
      }
      document.title = ''
      this.visualMode = mode
      this.$nextTick(() => {
        const printCSS = this.buildPrintCSS(mode === 'bw')
        document.head.insertAdjacentHTML('beforeend', printCSS)
        window.print()
        setTimeout(() => {
          const el = document.getElementById('print-styles')
          if (el) el.remove()
          document.title = originalTitle
          if (faviconEl && originalFaviconHref) {
            faviconEl.setAttribute('href', originalFaviconHref)
          }
          this.visualMode = previousMode
        }, 1000)
      })
    },
    printVoucher() {
      this.executePrint('color')
    },
    printVoucherBW() {
      this.executePrint('bw')
    },
    setVisualMode(mode) {
      this.visualMode = mode
    },
  },
  setup() {
    return {}
  },
}
</script>

<style lang="scss" scoped>
// Brand colors are generated from public/config/branding.json.
@import 'scss/brand-tokens';

$brand-light: rgba($brand-primary, 0.08);
$brand-dark: darken($brand-secondary, 5%);
$accent-blue: $brand-primary;
$accent-blue-dark: $brand-primary-dark;

.return-voucher-wrapper {
  padding: 1.5rem;
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
  min-height: 100vh;
  font-size: 16px;
}

.top-action-bar {
  background: white;
  padding: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  border-left: 4px solid $accent-blue;
}

.page-title {
  color: #1f1f1f;
  font-weight: 700;
  display: flex;
  align-items: center;
  margin-bottom: 0.25rem;
}

.page-subtitle {
  color: #718096;
  font-size: 0.875rem;
  margin: 0;
}

.action-buttons-top {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 768px) {
    justify-content: flex-start;
    margin-top: 1rem;
  }
}

.view-mode-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .view-mode-label {
    font-size: 0.875rem;
    font-weight: 600;
    color: $brand-secondary;
  }

  .btn-group {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    border-radius: 6px;

    .btn {
      font-size: 0.875rem;
      font-weight: 500;
      padding: 0.5rem 1rem;
      border: 1px solid #dee2e6;
      transition: all 0.3s ease;

      &:hover {
        transform: translateY(-1px);
      }

      &.btn-danger {
        background-color: $accent-blue;
        border-color: $accent-blue;
        color: white;
      }

      &.btn-dark {
        background-color: $brand-secondary;
        border-color: $brand-secondary;
        color: white;
      }
    }
  }
}

.action-btn-top {
  border-radius: 8px;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  transition: all 0.3s ease;
  border-color: rgba(13, 110, 253, 0.65);
  color: $accent-blue-dark;

  &:hover {
    background-color: rgba(13, 110, 253, 0.08);
    border-color: rgba(13, 110, 253, 0.75);
    color: $accent-blue-dark;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(var(--brand-secondary-rgb), 0.2);
  }

  &.dropdown .btn {
    border-color: rgba(13, 110, 253, 0.65);
    color: $accent-blue-dark;
    background-color: transparent;

    &:hover, &:focus {
      background-color: rgba(13, 110, 253, 0.08) !important;
      border-color: rgba(13, 110, 253, 0.75) !important;
      color: $accent-blue-dark !important;
      box-shadow: 0 4px 12px rgba(var(--brand-secondary-rgb), 0.2) !important;
    }

    &:active, &.active, &.show {
      background-color: rgba(13, 110, 253, 0.12) !important;
      border-color: rgba(13, 110, 253, 0.75) !important;
      color: $accent-blue-dark !important;
      box-shadow: 0 4px 12px rgba(var(--brand-secondary-rgb), 0.25) !important;
    }
  }

  .dropdown-menu {
    border-radius: 8px;
    border: 1px solid rgba(var(--brand-secondary-rgb), 0.2);
    box-shadow: 0 8px 25px rgba(var(--brand-secondary-rgb), 0.15);
    margin-top: 0.5rem;

    .dropdown-item {
      padding: 0.75rem 1rem;
      font-weight: 500;
      color: $brand-secondary;
      transition: all 0.2s ease;
      border-radius: 6px;
      margin: 0.25rem;

      &:hover, &:focus {
        background-color: rgba(var(--brand-secondary-rgb), 0.08) !important;
        color: $accent-blue-dark !important;
        transform: translateX(2px);
      }

      .feather-icon {
        color: $accent-blue-dark;
        transition: color 0.2s ease;
      }
    }
  }
}

.quick-stats {
  @media (max-width: 768px) {
    margin-left: 0 !important;
    margin-top: 0.5rem;
  }
}

.stat-item-inline {
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  margin-left: 0.5rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 600;

  svg {
    margin-right: 0.25rem;
  }

  &.info {
    background: rgba(var(--brand-secondary-rgb), 0.1);
    color: $brand-primary;
  }

  &.danger {
    background: rgba(13, 110, 253, 0.10);
    color: $accent-blue-dark;
  }

  &.primary {
    background: rgba(var(--brand-secondary-rgb), 0.1);
    color: $brand-primary;
  }

  &.success {
    background: rgba(40, 199, 111, 0.1);
    color: #28c76f;
  }

  &.warning {
    background: rgba(255, 159, 67, 0.1);
    color: #ff9f43;
  }
}

.modern-alert {
  border-radius: 12px;
  border: none;
  box-shadow: 0 4px 12px rgba(var(--brand-secondary-rgb), 0.15);
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
}

.loading-spinner {
  width: 3rem;
  height: 3rem;
}

.loading-text {
  color: #6c757d;
  font-size: 1.1rem;
}

.voucher-content {
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.voucher-card {
  border-radius: 16px;
  border: none;
  overflow: hidden;
  background: #fff;
}

.voucher-header {
  padding: 2rem;
  background: linear-gradient(135deg, rgba(13, 110, 253, 0.10) 0%, rgba(13, 110, 253, 0.06) 100%);
  color: #121212;
  margin: -1.25rem -1.25rem 2rem -1.25rem;
  border-left: 6px solid rgba(13, 110, 253, 0.6);
}

.company-logo {
  .logo-img {
    width: clamp(160px, 22vw, 300px);
    max-width: 100% !important;
    height: auto;
    background: transparent;
    padding: 0;
    border-radius: 0;
    box-shadow: none;
  }
}

.voucher-title {
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
}

.voucher-number {
  color: $accent-blue-dark;
}

.voucher-date {
  font-size: 1rem;
  opacity: 0.9;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.company-info-section {
  margin-bottom: 2rem;
}

.info-card {
  background: #f7fafc;
  border-radius: 12px;
  padding: 1.5rem;
  height: 100%;
  border: 1px solid #e2e8f0;
  border-left: 4px solid rgba(13, 110, 253, 0.6);
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  }
}

.info-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  color: $brand-secondary;
  font-weight: 600;
}

.info-content {
  .info-item {
    margin-bottom: 0.75rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  .info-subtitle {
    display: block;
    font-size: 0.875rem;
    color: #718096;
    font-style: italic;
  }

  .info-label {
    font-weight: 600;
    color: $brand-secondary;
    display: inline-block;
    min-width: 120px;
  }
}

.summary-section, .orders-section {
  margin-bottom: 2rem;
}

.section-title {
  display: flex;
  align-items: center;
  font-weight: 600;
  color: $brand-secondary;
  margin-bottom: 1rem;
}

.section-header {
  margin-bottom: 1rem;
}

.summary-table-container {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.modern-table {
  width: 100%;
  margin-bottom: 0;

  thead {
    background: linear-gradient(135deg, #000000 0%, #111111 100%);

    th {
      padding: 1rem;
      font-weight: 600;
      color: #ffffff;
      border: none;
      text-align: center;
      font-size: 0.875rem;
    }
  }

  tbody {
    tr {
      border-bottom: 1px solid #e2e8f0;

      &:last-child {
        border-bottom: none;
      }
    }

    td {
      padding: 1rem;
      text-align: center;
      vertical-align: middle;
      border: none;
    }
  }
}

.stat-badge {
  font-size: 1rem;
  padding: 0.5rem 0.75rem;
  font-weight: 600;
}

.amount {
  font-weight: 600;
  color: $brand-secondary;

  &.danger {
    color: $accent-blue-dark;
  }
}

.total-amount {
  font-size: 1.125rem;
  color: $brand-primary;
  font-weight: 700;
}

.table-container {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.modern-orders-table {
  margin-bottom: 0;

  thead th {
    background: #f7fafc;
    border-bottom: 2px solid #e2e8f0;
    font-weight: 600;
    color: $brand-secondary;
    padding: 1rem 0.75rem;
  }

  tbody {
    tr {
      transition: all 0.2s ease;

      &:hover {
        background-color: #f7fafc;
      }
    }

    td {
      padding: 0.75rem;
      border-color: #edf2f7;
      vertical-align: middle;
    }
  }
}

.order-id-cell {
  .order-number {
    color: $brand-primary;
    font-weight: 600;
    margin: 0;
  }

  .exchange-badge {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    margin-top: 0.25rem;
  }
}

.supplier-cell,
.customer-cell {
  display: flex;
  align-items: center;

  .supplier-name,
  .customer-name {
    font-weight: 500;
  }
}

.address-cell {
  .address-item {
    margin-bottom: 0.25rem;
    display: flex;
    align-items: center;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.phone-cell {
  .phone-item {
    margin-bottom: 0.25rem;
    display: flex;
    align-items: center;

    &:last-child {
      margin-bottom: 0;
    }
  }
}

.price-cell {
  text-align: right;

  .price-amount {
    font-weight: 600;
    color: #28c76f;
  }
}

.return-price-cell {
  text-align: right;

  .return-price-amount {
    font-weight: 600;
    color: $accent-blue-dark;
  }
}

.return-reasons-cell {
  max-width: 350px;
  min-width: 280px;
}

.reason-item {
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.reason-main {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 0.75rem;
  border-left: 3px solid rgba(13, 110, 253, 0.6);
}

.reason-text {
  margin-bottom: 0.5rem;
}

.reason-title {
  display: block;
  color: $accent-blue-dark;
  font-weight: 700;
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}

.reason-note {
  display: flex;
  align-items: flex-start;
  font-size: 0.8rem;
  color: #6c757d;
  font-style: italic;
  margin-top: 0.25rem;

  svg {
    margin-top: 0.125rem;
    flex-shrink: 0;
  }
}

.reason-meta {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
}

.reason-date {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  color: #495057;

  &.scheduled {
    color: #007bff;
  }

  svg {
    flex-shrink: 0;
  }
}

.date-label {
  font-weight: 600;
  margin-right: 0.25rem;
}

.date-value {
  font-weight: 500;
}

.reason-options {
  margin-top: 0.5rem;
}

.options-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.options-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.option-badge {
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
}

.reason-divider {
  height: 1px;
  background: #dee2e6;
  margin: 0.75rem 0;
}

.no-reason {
  display: flex;
  align-items: center;
  font-style: italic;
  color: #6c757d;
  font-size: 0.875rem;
}

.signature-section {
  margin-top: 3rem;
  padding: 2rem 0;
  border-top: 2px solid #e9ecef;
}

.signature-box {
  text-align: center;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
}

.signature-title {
  color: $brand-secondary;
  font-weight: 600;
  margin-bottom: 1rem;
}

.signature-line {
  width: 100%;
  height: 80px;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  background: #f8f9fa;
}

.totals-footer {
  margin-top: 2rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px;
  border: 1px solid #dee2e6;
}

.totals-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.total-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  &.highlight {
    background: linear-gradient(135deg, $brand-primary 0%, $brand-accent 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(var(--brand-secondary-rgb), 0.3);
  }

  .total-label {
    font-weight: 600;
    color: inherit;
  }

  .total-value {
    font-weight: 700;
    font-size: 1.125rem;

    &.primary {
      color: $brand-primary;
    }

    &.danger {
      color: $accent-blue-dark;
    }

    .highlight & {
      color: white;
    }
  }
}

// Black & White Mode Styling
.voucher-card--bw {
  .voucher-header {
    background: #fff !important;
    color: #000 !important;
    border: 2px solid #000;
    border-radius: 0;

    .voucher-title,
    .voucher-date {
      color: #000 !important;
    }

    .voucher-number {
      color: #000 !important;
      text-decoration: underline;
    }
  }

  .company-logo .logo-img {
    background: #fff !important;
    padding: 8px 12px;
    border: 2px solid #000;
    border-radius: 4px;
    box-shadow: none !important;
  }

  .info-card {
    background: #fff !important;
    border: 1px solid #000;
    border-left: 3px solid #000 !important;
  }

  .company-card {
    background: #fff !important;
    border: 1px solid #000 !important;
    border-left: 3px solid #000 !important;
    box-shadow: none !important;

    .info-header h5 {
      color: #000 !important;
    }

    .company-name {
      color: #000 !important;
    }

    .info-subtitle {
      color: #000 !important;
      font-style: italic;
    }

    .info-label {
      color: #000 !important;
    }

    .info-header {
      border-bottom: 1px solid #000 !important;
      color: #000 !important;
    }
  }

  .section-title {
    color: #000 !important;
    border-bottom: 2px solid #000;
    padding-bottom: 0.5rem;
  }

  .summary-table-container {
    border: 2px solid #000;
    box-shadow: none !important;
  }

  .modern-table thead {
    background: #f5f5f5 !important;

    th {
      color: #000 !important;
      border: 1px solid #000;
    }
  }

  .modern-table tbody {
    td {
      border: 1px solid #000;
    }
  }

  .table-container {
    border: 2px solid #000;
    box-shadow: none !important;
  }

  .modern-orders-table thead th {
    background: #f5f5f5 !important;
    color: #000 !important;
    border: 1px solid #000;
  }

  .modern-orders-table tbody td {
    border: 1px solid #000;
  }

  .stat-badge,
  .badge {
    background: #fff !important;
    color: #000 !important;
    border: 1px solid #000 !important;
  }

  .amount {
    color: #000 !important;

    &.danger,
    &.total-amount {
      color: #000 !important;
      font-weight: 700;
    }
  }

  .reason-main {
    background: #fff !important;
    border: 1px solid #000;
    border-left: 3px solid #000 !important;
  }

  .reason-title {
    color: #000 !important;
  }

  .signature-section {
    border-top: 2px solid #000;
  }

  .signature-line {
    border: 2px solid #000;
    background: #fff !important;
  }

  .totals-footer {
    background: #fff !important;
    border: 2px solid #000;
  }

  .total-item {
    background: #fff !important;
    border: 1px solid #000;

    &.highlight {
      background: #f0f0f0 !important;
      color: #000 !important;
      border: 2px solid #000 !important;

      .total-value {
        color: #000 !important;
      }
    }

    .total-value {
      &.primary,
      &.danger {
        color: #000 !important;
      }
    }
  }

  .order-number {
    color: #000 !important;
  }

  .price-cell .price-amount {
    color: #000 !important;
  }

  .return-price-cell .return-price-amount {
    color: #000 !important;
  }

  .feather-icon {
    color: #000 !important;
  }
}

// Color Mode Enhanced Styling
.voucher-card--color {
  .voucher-header {
    background: linear-gradient(135deg, rgba(13, 110, 253, 0.10) 0%, rgba(13, 110, 253, 0.06) 100%);
    position: relative;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: -50%;
      right: -10%;
      width: 300px;
      height: 300px;
      background: rgba(13, 110, 253, 0.08);
      border-radius: 50%;
    }

    &::after {
      content: '';
      position: absolute;
      bottom: -30%;
      left: -5%;
      width: 200px;
      height: 200px;
      background: rgba(13, 110, 253, 0.06);
      border-radius: 50%;
    }
  }

  .info-card {
    transition: all 0.3s ease;
    border-left: 4px solid rgba(13, 110, 253, 0.6);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    }
  }

  .modern-table thead {
    background: linear-gradient(135deg, #000000 0%, #111111 100%);
  }

  .stat-badge {
    transition: all 0.2s ease;

    &:hover {
      transform: scale(1.05);
    }
  }

  .reason-main {
    transition: all 0.2s ease;

    &:hover {
      transform: translateX(3px);
      box-shadow: 0 4px 12px rgba(var(--brand-secondary-rgb), 0.1);
    }
  }

  .total-item {
    transition: all 0.3s ease;

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    }

    &.highlight {
      background: linear-gradient(135deg, $brand-primary 0%, $brand-accent 100%);
      position: relative;
      overflow: hidden;

      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: -100%;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
        transition: left 0.5s ease;
      }

      &:hover::before {
        left: 100%;
      }
    }
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .return-reasons-cell {
    max-width: 250px;
    min-width: 200px;
  }

  .reason-main {
    padding: 0.5rem;
  }

  .reason-title {
    font-size: 0.8rem;
  }

  .reason-meta {
    .reason-date {
      font-size: 0.7rem;
    }
  }

  .options-list {
    .option-badge {
      font-size: 0.65rem;
      padding: 0.2rem 0.4rem;
    }
  }

  .action-buttons-top {
    gap: 0.5rem;
  }

  .view-mode-toggle {
    width: 100%;
    justify-content: space-between;
  }
}
</style>
